import { useEffect, useMemo, useState } from 'react';
import { App, DatePicker, Form, Popconfirm, Select, Space, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';
import { DashboardContainer, KPICardsGrid, StudentsHeaderLeft, StudentsHeaderRow, StudentsHeaderSub, StudentsHeaderTitle } from '../style';
import { formatMoney, getEntryDisplayDayjs, parseMoneyNumber } from './shared';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput } from '@/components/ui/AppInput';
import { Button } from '@components/ui/Button';
import { useAuthStore } from '@stores/auth.store';
import { FINANCE_ENTRY_TYPES, subscribeFinanceEntries } from '@services/registry';
import { useFinanceAccounts } from '@/hooks/tenant/finance/useFinanceAccounts';
import { useFinanceSummary } from '@/hooks/tenant/finance/useFinanceSummary';
import { usePostFinanceEntry } from '@/hooks/tenant/finance/usePostFinanceEntry';
import { useUpdateFinanceEntry } from '@/hooks/tenant/finance/useUpdateFinanceEntry';
import { useDeleteFinanceEntry } from '@/hooks/tenant/finance/useDeleteFinanceEntry';
import { useAbility } from '@/ability/AbilityProvider';
import dayjs from 'dayjs';

const { Text } = Typography;

export default function ReceiveLedger() {
    const { message } = App.useApp();
    const ability = useAbility();
    const canManage = !!ability?.can?.('manage', 'ReceiveLedger');
    const { dateFormat } = useTenantDateFormat();
    const [searchText, setSearchText] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [form] = Form.useForm();
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const { rows: accounts } = useFinanceAccounts({ tenantId });
    const { summary } = useFinanceSummary({ tenantId });
    const postMutation = usePostFinanceEntry({ tenantId });
    const updateMutation = useUpdateFinanceEntry({ tenantId });
    const deleteMutation = useDeleteFinanceEntry({ tenantId });
    const [entries, setEntries] = useState([]);
    const [accountFilter, setAccountFilter] = useState(undefined);
    const [editingEntry, setEditingEntry] = useState(null); // row object

    useEffect(() => {
        if (!createOpen) return;
        // Modal mounts Form after open; set defaults after mount.
        setTimeout(() => {
            form.setFieldsValue({
                date: dayjs(),
                accountId: form.getFieldValue('accountId') || accounts?.[0]?.id,
            });
        }, 0);
    }, [createOpen, form, accounts]);

    useEffect(() => {
        if (!tenantId) {
            setEntries([]);
            return;
        }
        const unsub = subscribeFinanceEntries({
            tenantId,
            type: FINANCE_ENTRY_TYPES.RECEIVE,
            accountId: accountFilter,
            onNext: (next) => setEntries(next),
            onError: (err) => console.error(err),
        });
        return () => unsub?.();
    }, [tenantId, accountFilter]);

    const filteredRows = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((r) => {
            const particulars = (r.particulars || '').toLowerCase();
            const accountName =
                (accounts || []).find((a) => a.id === r.accountId)?.name || '';
            return particulars.includes(q) || accountName.toLowerCase().includes(q);
        });
    }, [entries, searchText, accounts]);

    const totals = useMemo(() => {
        if (!tenantId) return { total: 0, openingBalance: 0 };
        if (accountFilter) {
            const acc = (accounts || []).find((a) => a.id === accountFilter) || null;
            return {
                total: Number(acc?.totalReceive || 0) || 0,
                openingBalance: Number(acc?.openingBalance || 0) || 0,
            };
        }
        const openingBalance = (accounts || []).reduce((s, a) => s + (Number(a.openingBalance || 0) || 0), 0);
        return {
            total: Number(summary?.totalReceive || 0) || 0,
            openingBalance,
        };
    }, [tenantId, accountFilter, summary, accounts]);

    const rowsWithAccount = useMemo(() => {
        const nameById = new Map((accounts || []).map((a) => [a.id, a.name || a.id]));
        return (filteredRows || []).map((r) => ({
            ...r,
            key: r.id,
            accountName: nameById.get(r.accountId) || '—',
        }));
    }, [filteredRows, accounts]);

    /** Pinned row so opening balance appears in the table even when there are no receipt entries. */
    const ledgerDataSource = useMemo(() => {
        const nameById = new Map((accounts || []).map((a) => [a.id, a.name || a.id]));
        const showOpeningSummary =
            Boolean(tenantId) &&
            (accounts || []).length > 0 &&
            (accountFilter != null || Math.abs(totals.openingBalance) > 0);

        const openingRow = showOpeningSummary
            ? {
                id: '__opening__',
                key: '__opening__',
                isOpeningBalanceRow: true,
                accountId: accountFilter,
                accountName: accountFilter ? nameById.get(accountFilter) || '—' : 'All accounts',
                particulars: 'Opening balance',
                amount: totals.openingBalance,
            }
            : null;

        const combined = openingRow ? [openingRow, ...rowsWithAccount] : rowsWithAccount;

        const q = (searchText || '').trim().toLowerCase();
        if (!q) return combined;

        return combined.filter((r) => {
            if (r.isOpeningBalanceRow) {
                const blob = `${r.particulars || ''} ${r.accountName || ''} opening`.toLowerCase();
                return blob.includes(q);
            }
            const particulars = (r.particulars || '').toLowerCase();
            const accountName = (r.accountName || '').toLowerCase();
            return particulars.includes(q) || accountName.includes(q);
        });
    }, [tenantId, accounts, accountFilter, totals.openingBalance, rowsWithAccount, searchText]);

    const columns = useMemo(() => ([
        {
            title: 'Date',
            key: 'date',
            render: (_, row) => {
                if (row.isOpeningBalanceRow) return '—';
                const d = getEntryDisplayDayjs(row);
                return d ? d.format(dateFormat) : '—';
            },
        },
        {
            title: 'Account',
            dataIndex: 'accountName',
            key: 'account',
            render: (v) => v || '—',
        },
        {
            title: 'Particulars',
            dataIndex: 'particulars',
            key: 'particulars',
            render: (v, row) => (row.isOpeningBalanceRow ? (v || 'Opening balance') : v || '—'),
        },
        {
            title: 'Amount',
            key: 'amount',
            render: (_, row) => (
                <Text strong={Boolean(row.isOpeningBalanceRow)} style={{ color: (Number(row.amount) || 0) > 0 ? '#16a34a' : undefined }}>
                    {row.amount ? formatMoney(row.amount) : '—'}
                </Text>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, row) => {
                if (row.isOpeningBalanceRow) return <Text type="secondary">—</Text>;
                if (!canManage) return <Text type="secondary">—</Text>;
                const busy = deleteMutation.isPending || updateMutation.isPending;
                return (
                    <Space size={10}>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditingEntry(row);
                                setCreateOpen(true);
                                setTimeout(() => {
                                    form.setFieldsValue({
                                        accountId: row.accountId,
                                        date: getEntryDisplayDayjs(row) || dayjs(),
                                        particulars: row.particulars || '',
                                        amount: formatMoney(row.amount || 0),
                                    });
                                }, 0);
                            }}
                            style={{ border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', padding: 0 }}
                            aria-label="Edit"
                        >
                            <EditOutlined />
                        </button>
                        <Popconfirm
                            title="Delete receive?"
                            description="This will adjust analytics and account totals."
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => void deleteMutation.mutateAsync({ entryId: row.id }).then(() => message.success('Receive deleted')).catch((e) => message.error(e?.message || 'Failed to delete'))}
                        >
                            <button
                                type="button"
                                disabled={busy}
                                onClick={(e) => e.stopPropagation()}
                                style={{ border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', padding: 0, color: '#ef4444' }}
                                aria-label="Delete"
                            >
                                <DeleteOutlined />
                            </button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ]), [dateFormat, canManage]);

    const openCreate = () => {
        form.resetFields();
        setEditingEntry(null);
        setCreateOpen(true);
    };

    const onCreate = async () => {
        if (!canManage) {
            message.error('You have view-only access for Receive Ledger.');
            return;
        }
        const v = await form.validateFields();
        const amt = parseMoneyNumber(v.amount);
        if (!amt || amt <= 0) {
            form.setFields([{ name: 'amount', errors: ['Amount must be greater than 0'] }]);
            return;
        }
        try {
            if (editingEntry?.id && editingEntry.id !== '__opening__') {
                await updateMutation.mutateAsync({
                    entryId: editingEntry.id,
                    patch: {
                        accountId: v.accountId,
                        date: v.date?.toDate?.() || v.date || new Date(),
                        particulars: (v.particulars || '').trim(),
                        amount: amt,
                    },
                });
                message.success('Receive updated');
            } else {
                await postMutation.mutateAsync({
                    accountId: v.accountId,
                    type: FINANCE_ENTRY_TYPES.RECEIVE,
                    date: v.date?.toDate?.() || v.date || new Date(),
                    particulars: (v.particulars || '').trim(),
                    amount: amt,
                });
                message.success('Receive saved');
            }
            setCreateOpen(false);
            setEditingEntry(null);
        } catch (e) {
            message.error(e?.message || (editingEntry?.id ? 'Failed to update receive' : 'Failed to save receive'));
        }
    };

    return (
        <DashboardContainer>
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Receive Ledger</StudentsHeaderTitle>
                    <StudentsHeaderSub>All receipts and income entries in one place.</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <Space size={10} wrap>
                    <Select
                        value={accountFilter}
                        onChange={(v) => setAccountFilter(v || undefined)}
                        placeholder="All accounts"
                        allowClear
                        style={{ minWidth: 220 }}
                        options={(accounts || []).map((a) => ({
                            value: a.id,
                            label: `${a.name}${a.type ? ` (${a.type})` : ''}`,
                        }))}
                    />
                    <Tooltip title={!accounts?.length ? 'Create an account first (Finance → Account Management).' : !canManage ? 'View-only access' : undefined}>
                        <span>
                            <Button type="primary" onClick={openCreate} disabled={!accounts?.length || !canManage}>
                                Add Receive
                            </Button>
                        </span>
                    </Tooltip>
                </Space>
            </StudentsHeaderRow>

            <KPICardsGrid $columns={2} $rows={1} $height="auto" style={{ marginBottom: 16 }}>
                <KPICard title="Opening balance" value={formatMoney(totals.openingBalance)} />
                <KPICard title="Total Receive" value={formatMoney(totals.total)} />
            </KPICardsGrid>

            <Table
                title="Ledger"
                columns={columns}
                dataSource={ledgerDataSource}
                loading={postMutation.isPending}
                emptyText="No receipts yet"
                searchValue={searchText}
                onSearchChange={setSearchText}
                pagination={true}
                pageSize={10}
                showSearch={true}
                showPagination={true}
            />

            <AppModal
                title={editingEntry?.id ? 'Edit receive' : 'Add receive'}
                open={createOpen}
                onCancel={() => {
                    setCreateOpen(false);
                    setEditingEntry(null);
                }}
                onOk={() => void onCreate()}
                okText={editingEntry?.id ? 'Save changes' : 'Save receive'}
                size="md"
                destroyOnClose
                okButtonProps={{ disabled: postMutation.isPending || updateMutation.isPending || !canManage }}
                confirmLoading={postMutation.isPending || updateMutation.isPending}
            >
                <Form
                    form={form}
                    layout="vertical"
                    preserve={false}
                    initialValues={{ amount: '', date: dayjs() }}
                >
                    <Form.Item
                        name="accountId"
                        label="Account"
                        rules={[{ required: true, message: 'Select an account' }]}
                    >
                        <Select
                            placeholder="Select account"
                            options={(accounts || []).map((a) => ({
                                value: a.id,
                                label: `${a.name}${a.type ? ` (${a.type})` : ''}`,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="date"
                        label="Date"
                        rules={[{ required: true, message: 'Select a date' }]}
                    >
                        <DatePicker style={{ width: '100%' }} format={dateFormat} />
                    </Form.Item>
                    <Form.Item
                        name="particulars"
                        label="Particulars"
                        rules={[{ required: true, message: 'Enter particulars' }]}
                    >
                        <AppInput placeholder="e.g. Tuition fee" />
                    </Form.Item>
                    <Form.Item
                        name="amount"
                        label="Amount"
                        rules={[{ required: true, message: 'Enter amount' }]}
                    >
                        <AppInput isPrice placeholder="0" />
                    </Form.Item>
                </Form>
            </AppModal>
        </DashboardContainer>
    );
}

